import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { listUserProfiles } from '../lib/firestore';
import { mergeFeaturedFarmers } from '../data/farmers';
import { mergeDoctorProfiles } from '../data/doctors';

function formatDoctorName(name = '') {
  const trimmed = String(name || '').trim();
  if (!trimmed) return '名称未設定';
  return trimmed.endsWith('先生') ? trimmed : `${trimmed} 先生`;
}

export default function AdminUsersPage() {
  const { isAdmin } = useAuth();
  const [farmers, setFarmers] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const roleLabel = isAdmin ? 'サイト管理者' : '運営';

  async function loadUsers() {
    setLoading(true);
    setError('');

    try {
      const [profiles, farmerPages, doctorPages] = await Promise.all([
        listUserProfiles(),
        Promise.resolve(mergeFeaturedFarmers([])),
        Promise.resolve(mergeDoctorProfiles([])),
      ]);
      const farmerPageMap = new Map(farmerPages.map((row) => [row.id, row]));
      const doctorPageMap = new Map(doctorPages.map((row) => [row.id, row]));

      setFarmers(
        profiles
          .filter((row) => row.role === 'farmer')
          .map((row) => ({
            uid: row.uid,
            name: row.name || '',
            email: row.email || '',
            contact: row.contact || '',
            position: row.position || '',
            linkedPageName: farmerPageMap.get(row.linkedFarmerId)?.name || '',
            linkedPageId: row.linkedFarmerId || '',
          }))
      );
      setDoctors(
        profiles
          .filter((row) => row.role === 'doctor')
          .map((row) => ({
            uid: row.uid,
            name: row.name || '',
            email: row.email || '',
            contact: row.contact || '',
            position: row.position || '',
            linkedPageName: doctorPageMap.get(row.linkedDoctorId)?.name || '',
            linkedPageId: row.linkedDoctorId || '',
          }))
      );
    } catch (err) {
      setError(`ユーザー一覧の読み込みに失敗しました。${err?.message ? ` ${err.message}` : ''}`);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadUsers();
  }, []);

  return (
    <>
      <Helmet><title>Drs.Farm | ユーザー一覧</title></Helmet>
      <h1>ユーザー一覧</h1>
      <p className="hint">ログイン権限: {roleLabel}</p>
      {error ? <p className="error-text">{error}</p> : null}

      <section className="panel">
        <div className="row-actions">
          <Link className="btn btn-secondary" to="/admin">管理者メニューへ戻る</Link>
          <button className="btn btn-outline-solid" type="button" onClick={loadUsers} disabled={loading}>
            {loading ? '更新中...' : '再読み込み'}
          </button>
        </div>
      </section>

      <section className="admin-grid">
        <article className="panel">
          <div className="directory-head">
            <div>
              <h2>農家一覧</h2>
              <p className="hint">登録済みの農家ユーザーです。</p>
            </div>
            <p className="directory-count">{farmers.length}件</p>
          </div>
          {loading ? <p>読み込み中...</p> : farmers.length === 0 ? <p>農家ユーザーはありません。</p> : (
            <ul className="directory-list">
              {farmers.map((farmer) => (
                <li key={farmer.uid} className="directory-row">
                  <div>
                    <p><strong>{farmer.name || '名称未設定'}</strong></p>
                    <p className="directory-meta">
                      ID: {farmer.email ? farmer.email.split('@')[0] : farmer.uid}
                    </p>
                    <p className="directory-meta">
                      農家区分: {farmer.position || '-'} / 連絡先: {farmer.contact || '-'}
                    </p>
                    <p className="directory-meta">紐付けページ: {farmer.linkedPageName || farmer.linkedPageId || '-'}</p>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </article>

        <article className="panel">
          <div className="directory-head">
            <div>
              <h2>ドクター一覧</h2>
              <p className="hint">登録済みのドクターです。</p>
            </div>
            <p className="directory-count">{doctors.length}件</p>
          </div>
          <div className="row-actions">
            <Link className="btn btn-secondary" to="/admin/doctors">Drs. Farmドクター関連を開く</Link>
          </div>
          {loading ? <p>読み込み中...</p> : doctors.length === 0 ? <p>ドクターはありません。</p> : (
            <ul className="directory-list">
              {doctors.map((doctor) => (
                <li key={doctor.uid} className="directory-row">
                  <div>
                    <p><strong>{formatDoctorName(doctor.name)}</strong></p>
                    <p className="directory-meta">
                      ID: {doctor.email ? doctor.email.split('@')[0] : doctor.uid} / 役職: {doctor.position || '-'}
                    </p>
                    <p className="directory-meta">
                      連絡先: {doctor.contact || '-'} / 紐付けページ: {doctor.linkedPageName || doctor.linkedPageId || '-'}
                    </p>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </article>
      </section>
    </>
  );
}
